import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  Book,
  ExternalLink,
  Eye,
  EyeOff,
  BarChart3,
  Search,
  ShoppingCart
} from 'lucide-react';
import { AreaAutocomplete } from '@/components/AreaSelector';

const AdminBooksPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [stats, setStats] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    cover_url: '',
    amazon_link: '',
    mercado_livre_link: '',
    knowledge_areas: [],
    all_areas: false,
    description: '',
    priority: 0
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchBooks();
  }, [user, navigate]);

  const fetchBooks = async () => {
    try {
      const response = await api.get('/api/books?active_only=false');
      setBooks(response.data.books);
    } catch (error) {
      toast.error('Erro ao carregar livros');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/books/admin/stats');
      setStats(response.data);
      setShowStats(true);
    } catch (error) {
      toast.error('Erro ao carregar estatísticas');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.author || !formData.cover_url) {
      toast.error('Preencha título, autor e URL da capa');
      return;
    }
    
    if (!formData.amazon_link && !formData.mercado_livre_link) {
      toast.error('Adicione pelo menos um link de afiliado (Amazon ou Mercado Livre)');
      return;
    }
    
    try {
      if (editingBook) {
        await api.put(`/api/books/${editingBook.book_id}`, formData);
        toast.success('Livro atualizado!');
      } else {
        await api.post('/api/books', formData);
        toast.success('Livro cadastrado!');
      }
      
      resetForm();
      fetchBooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar livro');
    }
  };

  const handleEdit = (book) => {
    setEditingBook(book);
    setFormData({
      title: book.title,
      author: book.author,
      cover_url: book.cover_url,
      amazon_link: book.amazon_link || '',
      mercado_livre_link: book.mercado_livre_link || '',
      knowledge_areas: book.knowledge_areas || [],
      all_areas: book.all_areas || false,
      description: book.description || '',
      priority: book.priority || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (bookId) => {
    if (!window.confirm('Tem certeza que deseja excluir este livro?')) return;
    
    try {
      await api.delete(`/api/books/${bookId}`);
      toast.success('Livro excluído!');
      fetchBooks();
    } catch (error) {
      toast.error('Erro ao excluir livro');
    }
  };

  const toggleActive = async (book) => {
    try {
      await api.put(`/api/books/${book.book_id}`, { active: !book.active });
      toast.success(book.active ? 'Livro desativado' : 'Livro ativado');
      fetchBooks();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      author: '',
      cover_url: '',
      amazon_link: '',
      mercado_livre_link: '',
      knowledge_areas: [],
      all_areas: false,
      description: '',
      priority: 0
    });
    setEditingBook(null);
    setShowForm(false);
  };

  const filteredBooks = books.filter(book => 
    book.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    book.author.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-[#002FA7] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      {/* Header */}
      <header className="bg-white border-b border-[#E4E4E7] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-2">
                <Book className="h-6 w-6 text-[#002FA7]" />
                <h1 className="text-xl font-bold">Livros Afiliados</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={fetchStats}
                className="gap-2"
              >
                <BarChart3 className="h-4 w-4" />
                Estatísticas
              </Button>
              <Button
                onClick={() => { resetForm(); setShowForm(true); }}
                className="bg-[#002FA7] hover:bg-[#001f75] gap-2"
              >
                <Plus className="h-4 w-4" />
                Novo Livro
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats Modal */}
        {showStats && stats && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold">Estatísticas de Cliques</h2>
                  <Button variant="ghost" size="sm" onClick={() => setShowStats(false)}>✕</Button>
                </div>
                
                {/* Totals */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-orange-50 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-orange-600">{stats.totals.amazon}</p>
                    <p className="text-sm text-orange-700">Cliques Amazon</p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-yellow-600">{stats.totals.mercado_livre}</p>
                    <p className="text-sm text-yellow-700">Cliques ML</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">{stats.totals.total}</p>
                    <p className="text-sm text-blue-700">Total</p>
                  </div>
                </div>
                
                {/* Books ranking */}
                <div className="space-y-2">
                  {stats.books.map((book, index) => (
                    <div key={book.book_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-gray-400">#{index + 1}</span>
                        <div>
                          <p className="font-medium text-sm">{book.title}</p>
                          <p className="text-xs text-gray-500">{book.author}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-orange-600">Amazon: {book.clicks_amazon}</span>
                        <span className="text-yellow-600">ML: {book.clicks_mercado_livre}</span>
                        <span className="font-bold">Total: {book.total_clicks}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6 mb-6">
            <h2 className="text-lg font-bold mb-4">
              {editingBook ? 'Editar Livro' : 'Novo Livro'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Título *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Metodologia do Trabalho Científico"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Autor *</label>
                  <Input
                    value={formData.author}
                    onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                    placeholder="Antônio Joaquim Severino"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">URL da Capa *</label>
                <Input
                  value={formData.cover_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, cover_url: e.target.value }))}
                  placeholder="https://m.media-amazon.com/images/I/..."
                />
                {formData.cover_url && (
                  <img 
                    src={formData.cover_url} 
                    alt="Preview" 
                    className="mt-2 h-32 object-contain rounded border"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    <span className="flex items-center gap-2">
                      <ShoppingCart className="h-4 w-4 text-orange-500" />
                      Link Amazon
                    </span>
                  </label>
                  <Input
                    value={formData.amazon_link}
                    onChange={(e) => setFormData(prev => ({ ...prev, amazon_link: e.target.value }))}
                    placeholder="https://amzn.to/3xyz ou link completo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    <span className="flex items-center gap-2">
                      <ShoppingCart className="h-4 w-4 text-yellow-500" />
                      Link Mercado Livre
                    </span>
                  </label>
                  <Input
                    value={formData.mercado_livre_link}
                    onChange={(e) => setFormData(prev => ({ ...prev, mercado_livre_link: e.target.value }))}
                    placeholder="https://mercadolivre.com.br/..."
                  />
                </div>
              </div>
              
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.all_areas}
                      onChange={(e) => setFormData(prev => ({ ...prev, all_areas: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300 text-[#002FA7] focus:ring-[#002FA7]"
                    />
                    <span className="text-sm font-medium text-green-700">
                      Livro geral - aparece em TODAS as chamadas
                    </span>
                  </label>
                </div>
                
                {!formData.all_areas && (
                  <>
                    <label className="block text-sm font-medium mb-1">Áreas do Conhecimento</label>
                    <AreaAutocomplete
                      value={formData.knowledge_areas}
                      onChange={(areas) => setFormData(prev => ({ ...prev, knowledge_areas: areas }))}
                      placeholder="Buscar e adicionar área..."
                      maxSelections={10}
                      label=""
                    />
                  </>
                )}
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Descrição (opcional)</label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Breve descrição do livro"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Prioridade (0-10)</label>
                  <Input
                    type="number"
                    min="0"
                    max="10"
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button type="submit" className="bg-[#002FA7] hover:bg-[#001f75]">
                  {editingBook ? 'Salvar Alterações' : 'Cadastrar Livro'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por título ou autor..."
              className="pl-10"
            />
          </div>
        </div>

        {/* Books List */}
        <div className="bg-white border border-[#E4E4E7] rounded-lg">
          <div className="p-4 border-b border-[#E4E4E7]">
            <p className="text-sm text-gray-600">
              {filteredBooks.length} livro(s) cadastrado(s)
            </p>
          </div>
          
          {filteredBooks.length === 0 ? (
            <div className="p-12 text-center">
              <Book className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Nenhum livro cadastrado</p>
              <Button
                onClick={() => setShowForm(true)}
                className="mt-4 bg-[#002FA7] hover:bg-[#001f75]"
              >
                Cadastrar primeiro livro
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-[#E4E4E7]">
              {filteredBooks.map((book) => (
                <div key={book.book_id} className="p-4 flex gap-4">
                  {/* Cover */}
                  <div className="flex-shrink-0">
                    <img
                      src={book.cover_url}
                      alt={book.title}
                      className="w-20 h-28 object-cover rounded shadow"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/80x112?text=Sem+Capa';
                      }}
                    />
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold line-clamp-1">{book.title}</h3>
                        <p className="text-sm text-gray-600">{book.author}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        {!book.active && (
                          <Badge variant="secondary" className="bg-gray-200">Inativo</Badge>
                        )}
                        {book.priority > 0 && (
                          <Badge className="bg-purple-100 text-purple-700">
                            Prioridade {book.priority}
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {/* Areas */}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {book.all_areas ? (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          Todas as áreas
                        </Badge>
                      ) : (
                        <>
                          {book.knowledge_areas?.slice(0, 3).map((area, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {area}
                            </Badge>
                          ))}
                          {book.knowledge_areas?.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{book.knowledge_areas.length - 3}
                            </Badge>
                          )}
                        </>
                      )}
                    </div>
                    
                    {/* Links & Stats */}
                    <div className="flex items-center gap-4 mt-3 text-sm">
                      {book.amazon_link && (
                        <a
                          href={book.amazon_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-orange-600 hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Amazon ({book.clicks_amazon || 0})
                        </a>
                      )}
                      {book.mercado_livre_link && (
                        <a
                          href={book.mercado_livre_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-yellow-600 hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Mercado Livre ({book.clicks_mercado_livre || 0})
                        </a>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleActive(book)}
                      title={book.active ? 'Desativar' : 'Ativar'}
                    >
                      {book.active ? (
                        <Eye className="h-4 w-4 text-green-600" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(book)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(book.book_id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminBooksPage;
